import { MessagingTopology } from "@internal/open-catalog";

export interface RabbitMQExchangeDefinition {
  name: string,
  type: string,
  durable: boolean,
  auto_delete: boolean,
  internal: boolean,
  arguments: any;
}

export interface RabbitMQQueueDefinition {
  name: string,
  durable: boolean,
  auto_delete: boolean,
  arguments: any;
};

export interface RabbitMQBindingDefinition {
  source: string,
  destination: string,
  destination_type: string,
  routing_key: string,
  arguments: any;
};

export interface RabbitMQDefinition {
  rabbit_version: string,
  rabbitmq_version: string,
  product_name: string,
  product_version: string,
  rabbitmq_definition_format: string,
  original_vhost_name: string,
  explanation: string,
  description: string,
  default_queue_type: string,
  limits: any[],
  parameters: any[],
  policies: any[],
  metadata: {
    description: string,
    tags: any[],
    default_queue_type: string,
  },
  queues: RabbitMQQueueDefinition[],
  exchanges: RabbitMQExchangeDefinition[],
  bindings: RabbitMQBindingDefinition[]
}

const emptyDefinition: RabbitMQDefinition = {
  rabbit_version: '4.0.5',
  rabbitmq_version: '4.0.5',
  product_name: 'RabbitMQ',
  product_version: '4.0.5',
  rabbitmq_definition_format: 'single_virtual_host',
  original_vhost_name: '/',
  explanation: 'Definitions for the service messaging topology.',
  description: '',
  default_queue_type: 'quorum',
  limits: [],
  parameters: [],
  policies: [],
  metadata: {
    description: '',
    tags: [],
    default_queue_type: 'quorum'
  },
  queues: [],
  exchanges: [],
  bindings: []
}

const emptyExchangeDefinition: RabbitMQExchangeDefinition = {
  name: '',
  type: '',
  durable: true,
  auto_delete: false,
  internal: false,
  arguments: {}
}

const emptyQueueDefinition: RabbitMQQueueDefinition = {
  name: '',
  durable: true,
  auto_delete: false,
  arguments: {}
}

const emptyBindingDefinition: RabbitMQBindingDefinition = {
  source: '',
  destination: '',
  destination_type: '',
  routing_key: '',
  arguments: {}
}

export const exportToRabbitMQ = (topology?: MessagingTopology, vhost?: string): RabbitMQDefinition => {

  if (!topology) {
    return {} as RabbitMQDefinition;
  }

  const queues: RabbitMQQueueDefinition[] = [];
  const exchanges: RabbitMQExchangeDefinition[] = [];
  const bindings: RabbitMQBindingDefinition[] = [];

  if (topology.parkingLot) {
    const pl = topology.parkingLot;
    queues.push({ ...emptyQueueDefinition, name: pl.name, arguments: { 'x-queue-type': pl.type, ...pl.arguments } })
  }

  if (topology.input) {

    const input = topology.input;
    exchanges.push({ ...emptyExchangeDefinition, name: input.name, type: input.type, arguments: input.arguments });

    for (const iq of input.targets || []) {

      queues.push({
        ...emptyQueueDefinition, name: iq.name, arguments: {
          'x-dead-letter-exchange': '',
          'x-dead-letter-routing-key': iq.wait ? iq.wait.name : `${iq.name}.wait`,
          'x-queue-type': iq.type,
          ...iq.arguments
        }
      });

      if (iq.wait) {
        queues.push({
          ...emptyQueueDefinition, name: iq.wait.name, arguments: {
            'x-dead-letter-exchange': '',
            'x-dead-letter-routing-key': iq.name,
            'x-queue-type': iq.wait.type,
            'x-message-ttl': 1000,
            ...iq.wait.arguments
          }
        });
      }

      for (const iqb of iq.bindings || []) {
        bindings.push({
          ...emptyBindingDefinition,
          source: input.name,
          destination: iq.name,
          destination_type: iqb.type,
          arguments: iqb.arguments
        });
      }
    }

    for (const iex of input.sources || []) {

      exchanges.push({ ...emptyExchangeDefinition, name: iex.name, type: iex.type, arguments: iex.arguments });

      for (const iexb of iex.bindings || []) {
        bindings.push({
          ...emptyBindingDefinition,
          source: iex.name,
          destination: input.name,
          destination_type: iexb.type,
          arguments: iexb.arguments
        });
      }
    }
  }

  if (topology.output) {

    const output = topology.output;
    exchanges.push({ ...emptyExchangeDefinition, name: output.name, type: output.type, arguments: output.arguments });

    for (const oc of output.targets || []) {

       if (oc.name === `${output.name}.overseer`) {

        queues.push({
          ...emptyQueueDefinition, name: oc.name, arguments: oc.arguments
        });

        queues.push({
          ...emptyQueueDefinition, name: `${oc.name}.parking-lot`, arguments: {
            'x-queue-type': 'classic'
          }
        });

      } else {
        exchanges.push({ ...emptyExchangeDefinition, name: oc.name, type: oc.type, arguments: oc.arguments });
      }

      for (const ocb of oc.bindings || []) {
        bindings.push({
          ...emptyBindingDefinition,
          source: output.name,
          destination: oc.name,
          destination_type: ocb.type,
          arguments: ocb.arguments
        });
      }
    }
  }

  const original_vhost_name = vhost ? vhost : '/';
  
  if (topology.delayedQueue) {
    const q = topology.delayedQueue;
    queues.push({
      ...emptyQueueDefinition, name: q.name, arguments: {
        'x-dead-letter-exchange': `${topology.channelName}-input`,
        'x-dead-letter-routing-key': '',
        'x-queue-type': q.type,
        ...q.arguments
      }
    })
  }

  return { ...emptyDefinition, original_vhost_name, queues, exchanges, bindings };
}